import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import React from 'react';
import PracticeCharacterizationGraphTooltip from './PracticeCharacterizationGraphTooltip';
import constants from '@shared-styles/constants.module.scss';
import NotEnoughDataMessage from '../../shared/components/NotEnoughDataMessage';
import { getXAxisLabel, getYAxisLabel } from '../../shared/components/GraphLabels';
import { itemSorterFn } from '../../../utils/chartUtils';

const LEGEND_ORDER = {
  Satisfied: 1,
  Partial: 2,
  Deficient: 3,
  Unit: 4
};

const buildPracticeData = practiceArea => {
  if (!practiceArea || !practiceArea.Practices) {
    return [];
  }

  const data = [...practiceArea.Practices];
  data.sort((a, b) => a.Level - b.Level || a.NumberInLevel - b.NumberInLevel);
  return data.map(practice => ({
    ...practice,
    Acronym: practiceArea.Acronym,
    Unit: getUnitPerformance(practice)
  }));
};

const getUnitPerformance = practice => {
  switch (practice.Evaluation) {
    case 0:
      return practice.Satisfied / 2;
    case 1:
      return practice.Satisfied + practice.Partial / 2;
    case 2:
      return practice.Satisfied + practice.Partial + practice.Deficient / 2;
    default:
      break;
  }
};

/**
 * @typedef {object} PracticeCharacterizationChartProps
 * @property {Object} [practiceArea]
 * @property {(data: import('recharts').BarRectangleItem) => void} [practiceClick]
 * @property {boolean} [showPracticeCharacterizationForUnit]
 */

/**
 * @param {PracticeCharacterizationChartProps} props
 */
const PracticeCharacterizationChart = props => {
  const { practiceClick, practiceArea = {}, showPracticeCharacterizationForUnit = true } = props;

  const data = buildPracticeData(practiceArea);
  const totalData = Math.max(
    ...data.map(practice => practice.Satisfied + practice.Partial + practice.Deficient)
  );

  return (
    <div
      onClick={e => e.stopPropagation()}
      className='practice-characterization-graph'
      data-testid='practice-characterization-graph'>
      {totalData <= 2 ? <NotEnoughDataMessage /> : null}
      <ResponsiveContainer width='95%' height={400}>
        <ComposedChart
          data={data}
          barCategoryGap='25%'
          margin={{
            top: 20,
            right: 20,
            bottom: 20,
            left: -10
          }}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis dataKey='Name'>{getXAxisLabel('Practices within PA')}</XAxis>
          {totalData >= 3 && <Tooltip content={<PracticeCharacterizationGraphTooltip />} />}
          <YAxis allowDecimals={false} domain={[0, totalData]}>
            {getYAxisLabel('# Appraisals containing PA', 0, 25)}
          </YAxis>
          <Legend
            wrapperStyle={{ paddingTop: '1.5rem' }}
            align='center'
            itemSorter={itemSorterFn(LEGEND_ORDER)}
          />
          {totalData >= 3 && (
            <Bar
              dataKey='Satisfied'
              name='Satisfied'
              stackId='a'
              fill={constants.practiceSatisfiedColor}
              cursor='pointer'
              onClick={data => practiceClick(data)}
            />
          )}
          {totalData >= 3 && (
            <Bar
              dataKey='Partial'
              name='Partial'
              stackId='a'
              fill={constants.practicePartialColor}
              cursor='pointer'
              onClick={data => practiceClick(data)}
            />
          )}
          {totalData >= 3 && (
            <Bar
              dataKey='Deficient'
              name='Deficient'
              stackId='a'
              fill={constants.practiceDeficientColor}
              cursor='pointer'
              onClick={data => practiceClick(data)}
            />
          )}
          {totalData >= 3 && showPracticeCharacterizationForUnit ? (
            <Line
              name='Unit'
              type='linear'
              dot={{ stroke: constants.characterizationOrgAndUnitColor, strokeWidth: 2 }}
              dataKey='Unit'
              stroke={constants.characterizationOrgAndUnitColor}
              fill={constants.characterizationOrgAndUnitColor}
            />
          ) : null}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PracticeCharacterizationChart;
